/**
 * Fill in the blanks — drag-and-drop mode player
 */

import * as React from 'react';
import {
    DndContext,
    DragOverlay,
    type DragEndEvent,
    type DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    cn,
    DraggableItem,
    DroppableZone,
    Button,
    shuffleArray,
} from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import type { FITBElementData, FITBAnswer } from './schema';
import { shouldShuffleOptions, fitbElementSchema } from './schema';
import { renderPassageWithBlanks } from './PassageRenderer';
import { resolveFitbPerBlankOk } from './perBlankFeedback';
import { GripVertical, X } from 'lucide-react';
import map from 'lodash/map';
import trim from 'lodash/trim';
import filter from 'lodash/filter';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export type FITBPlayerDragProps = ElementPlayerProps<
    FITBElementData,
    FITBAnswer
> & {
    flashBlankId?: string | null;
    /** When false, hide generic i18n drag hint (parent already shows rubric). */
    showGenericDragInstructions?: boolean;
};

function buildChips(data: FITBElementData): { id: string; label: string }[] {
    const out: { id: string; label: string }[] = [];
    let n = 0;
    map(data.blanks, (b) => {
        map(
            filter(b.acceptedAnswers, (a) => trim(String(a)) !== ''),
            (a) => {
                out.push({ id: `chip-${n++}`, label: trim(String(a)) });
            }
        );
        map(
            filter(b.distractors ?? [], (d) => trim(String(d)) !== ''),
            (d) => {
                out.push({ id: `chip-${n++}`, label: trim(String(d)) });
            }
        );
    });
    return out;
}

function placementToAnswer(
    placement: Record<string, string>,
    chips: { id: string; label: string }[]
): FITBAnswer {
    const out: FITBAnswer = {};
    map(Object.entries(placement), ([blankId, chipId]) => {
        const c = chips.find((ch) => ch.id === chipId);
        if (c) out[blankId] = c.label;
    });
    return out;
}

function hydratePlacement(
    answer: FITBAnswer | undefined,
    chips: { id: string; label: string }[],
    blankIds: string[]
): Record<string, string> {
    const next: Record<string, string> = {};
    const used = new Set<string>();
    map(blankIds, (bid) => {
        const lab = answer?.[bid];
        if (!lab) return;
        const c = chips.find((ch) => ch.label === lab && !used.has(ch.id));
        if (c) {
            next[bid] = c.id;
            used.add(c.id);
        }
    });
    return next;
}

// Module-level default: an inline `answer = {}` is a NEW object every render, which re-ran the
// hydrate effect below -> setPlacement -> render -> ... ("Maximum update depth exceeded").
const EMPTY_ANSWER: FITBAnswer = {};

export const FITBPlayerDrag: React.FC<FITBPlayerDragProps> = ({
    data,
    answer = EMPTY_ANSWER,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    config,
    elementChildLabels,
    onFitbBlankAnchor,
    flashBlankId = null,
    schema: schemaProp,
    language,
    showGenericDragInstructions = true,
}) => {
    const schema = schemaProp ?? fitbElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const review = showFeedback || isReview;
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false hides explanation/hints.
    const revealCorrect = config?.behavior?.showCorrectAnswer !== false;
    // ELMS-2370: hide answer => no per-blank right/wrong marks (learner choice still shown).
    const perOk = revealCorrect ? resolveFitbPerBlankOk(result, data, answer ?? {}, review) : undefined;
    const shuffle = shouldShuffleOptions(config);

    const dataKey = React.useMemo(
        () => JSON.stringify({ blanks: data.blanks, passage: data.passage }),
        [data.blanks, data.passage]
    );

    const chips = React.useMemo(
        () => {
            const raw = buildChips(data);
            return shuffle ? shuffleArray([...raw]) : raw;
        },
        // dataKey encodes passage/blanks; listing `data` would reshuffle when parent passes a new ref after each answer
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataKey, shuffle]
    );

    const blankIds = React.useMemo(() => map(data.blanks, (_b, i) => `blank-${i}`), [data.blanks]);

    const [placement, setPlacement] = React.useState<Record<string, string>>({});
    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);

    React.useEffect(() => {
        setPlacement(hydratePlacement(answer, chips, blankIds));
    }, [answer, chips, blankIds]);

    const publish = React.useCallback(
        (next: Record<string, string>) => {
            setPlacement(next);
            onAnswer?.(placementToAnswer(next, chips));
        },
        [chips, onAnswer]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        })
    );

    const handleDragStart = React.useCallback(
        (event: DragStartEvent) => {
            if (disabled || review) return;
            setActiveDragId(String(event.active.id));
        },
        [disabled, review]
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            setActiveDragId(null);
            if (disabled || review) return;
            const { active, over } = event;
            if (!over) return;
            const overId = String(over.id);
            if (!overId.startsWith('drop-')) return;
            const blankId = overId.replace('drop-', '');
            const chipId = String(active.id);
            const chip = chips.find((c) => c.id === chipId);
            if (!chip) return;
            const next = { ...placement };
            for (const bid of Object.keys(next)) {
                if (next[bid] === chipId) delete next[bid];
            }
            next[blankId] = chipId;
            publish(next);
        },
        [disabled, review, placement, chips, publish]
    );

    const handleDragCancel = React.useCallback(() => {
        setActiveDragId(null);
    }, []);

    const handleClearBlank = React.useCallback(
        (blankId: string) => {
            if (disabled || review) return;
            const next = { ...placement };
            delete next[blankId];
            publish(next);
        },
        [disabled, review, placement, publish]
    );

    const chipUsed = React.useCallback(
        (chipId: string) => Object.values(placement).includes(chipId),
        [placement]
    );

    const isDragActive = activeDragId !== null;
    const activeDragChip = activeDragId ? chips.find((c) => c.id === activeDragId) : undefined;

    return (
        <div className={cn('fitb-player fitb-player-drag space-y-4', className)}>
            <div className="fitb-player-drag-passage rounded-lg border-0 bg-card p-4 shadow-sm">
                {showGenericDragInstructions ? (
                    <p className="fitb-player-drag-instructions text-sm text-muted-foreground mb-3">
                        {t('fitbDragInstructions')}
                    </p>
                ) : null}
                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <div className="fitb-player-drag-body space-y-4">
                        <div className="fitb-player-drag-bank flex flex-wrap gap-2 justify-center sm:justify-start">
                            {map(chips, (chip) => (
                                <DraggableItem
                                    key={chip.id}
                                    id={chip.id}
                                    useDragOverlay
                                    disabled={disabled || review}
                                    state={chipUsed(chip.id) ? 'used' : 'default'}
                                    // EAA-1680: small drag token, not a
                                    // row-sized option — use the chip surface
                                    // for the playful skin's pressable hooks.
                                    surface="chip"
                                    className="fitb-player-drag-chip flex items-center gap-2 text-sm"
                                >
                                    <GripVertical
                                        className="h-4 w-4 shrink-0 text-muted-foreground"
                                        aria-hidden
                                    />
                                    <span>{chip.label}</span>
                                </DraggableItem>
                            ))}
                        </div>
                        <div className="fitb-player-drag-passage-text border-t border-border pt-4">
                            {renderPassageWithBlanks(data.passage, {
                                renderBlank: (idx, id) => {
                                    const chipId = placement[id];
                                    const label = chipId
                                        ? chips.find((c) => c.id === chipId)?.label
                                        : '';
                                    const ok = perOk?.[id];
                                    const show = review && ok !== undefined;
                                    const dropState = show
                                        ? ok
                                            ? 'correct'
                                            : 'incorrect'
                                        : 'default';
                                    const childN = elementChildLabels?.fitbBlank?.[id];
                                    return (
                                        <span
                                            ref={(el) => onFitbBlankAnchor?.(id, el)}
                                            className={cn(
                                                'inline-flex items-baseline gap-0.5 align-baseline max-w-full',
                                                flashBlankId === id &&
                                                    'rounded-sm ring-2 ring-primary/50 ring-offset-1 transition-[box-shadow] duration-300'
                                            )}
                                        >
                                            {childN != null ? (
                                                <span
                                                    className="inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md bg-muted-foreground/15 text-sm font-bold text-foreground"
                                                    aria-hidden
                                                >
                                                    {childN}
                                                </span>
                                            ) : null}
                                            <DroppableZone
                                                id={`drop-${id}`}
                                                disabled={disabled || review}
                                                state={dropState}
                                                isDragActive={isDragActive}
                                                // EAA-1680: inline blank slot, not a
                                                // row-sized option — chip surface for
                                                // the playful skin's pressable hooks.
                                                surface="chip"
                                                className={cn(
                                                    'fitb-player-drag-slot inline-flex min-h-8 min-w-[15ch] max-w-full align-baseline mx-0.5 px-1',
                                                    !label && 'min-w-[6rem]'
                                                )}
                                            >
                                                {/*
                                                    EAA-1743: the label must read as centered
                                                    in the block regardless of the clear "×"
                                                    button, not merely left-aligned before it.
                                                    Take the button out of flex flow (absolute,
                                                    pinned to the right edge) so justify-center
                                                    centers the label alone; pr-6 (~button
                                                    width) reserves room so long labels never
                                                    sit under the button.
                                                */}
                                                <span
                                                    className={cn(
                                                        'fitb-player-drag-slot-inner relative flex w-full items-center justify-center gap-1',
                                                        !review && 'pr-6'
                                                    )}
                                                >
                                                    {label && (
                                                        <>
                                                            <span className="text-sm">{label}</span>
                                                            {!review && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="fitb-player-drag-slot-clear absolute right-0 top-1/2 h-6 w-6 shrink-0 -translate-y-1/2 rounded-full"
                                                                    onClick={() => handleClearBlank(id)}
                                                                    // EAA-2067: same running number as the visible badge.
                                                                    aria-label={t('fitbRemoveAnswerAria').replace(
                                                                        '{n}',
                                                                        String(childN ?? idx + 1)
                                                                    )}
                                                                >
                                                                    <X className="h-3.5 w-3.5" />
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </span>
                                            </DroppableZone>
                                        </span>
                                    );
                                },
                            })}
                        </div>
                    </div>
                    <DragOverlay dropAnimation={null}>
                        {activeDragChip ? (
                            <div className="fitb-player-drag-overlay pointer-events-none rounded-sm border border-primary/60 bg-background p-2 shadow-xl flex items-center gap-2 text-sm cursor-grabbing">
                                <GripVertical
                                    className="h-4 w-4 shrink-0 text-muted-foreground"
                                    aria-hidden
                                />
                                <span>{activeDragChip.label}</span>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </div>
            {review && revealCorrect && data.explanation ? (
                <p className="fitb-player-drag-explanation text-sm text-muted-foreground border-t border-border pt-3">
                    {data.explanation}
                </p>
            ) : null}
        </div>
    );
};
